import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { RefreshCw, Activity } from 'lucide-react';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';

const StringCombinerBoxAvailability = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState('39');
  const [blocks, setBlocks] = useState(['39', '40', '41', '42']);
  const [heatmapData, setHeatmapData] = useState({});
  const [componentStatus, setComponentStatus] = useState({
    inverter: { active: 1056, total: 1056 },
    string: { active: 4738, total: 4752 },
    tracker: { active: 18356, total: 18508 },
    statcom: { active: 20, total: 20 },
    pqm: { active: 10, total: 10 }
  });
  const [summary, setSummary] = useState({ average: 0, best: 0, worst: 0 });

  useEffect(() => {
    fetchData();
  }, [selectedBlock]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getStringCombinerBoxAvailability({
        plant_id: API_CONFIG.PLANT_ID,
        block: selectedBlock
      });
      
      if (response.data) {
        setHeatmapData(response.data.heatmaps || {});
        setSummary({
          average: response.data.average || 0,
          best: response.data.best || 0,
          worst: response.data.worst || 0
        });
      }
    } catch (error) {
      console.error('Error fetching string combiner box availability:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getColorClass = (value) => {
    if (value === 0) return 'bg-red-500 text-white';
    if (value >= 88) return 'bg-green-500 text-white';
    if (value >= 86) return 'bg-orange-500 text-white';
    if (value >= 84) return 'bg-orange-300 text-gray-800';
    return 'bg-red-500 text-white';
  };

  const generateHeatmapData = (inverterNum) => {
    const data = heatmapData[`inverter_${inverterNum}`];
    if (data && data.length > 0) return data;
    
    // Generate mock data: 3 rows x 14 columns = 42 cells per inverter
    return Array.from({ length: 42 }, (_, i) => {
      const rand = Math.random();
      if (rand < 0.1) return 0;
      if (rand < 0.3) return 83 + Math.random() * 1;
      if (rand < 0.5) return 84 + Math.random() * 2;
      if (rand < 0.8) return 86 + Math.random() * 2;
      return 88 + Math.random() * 2;
    });
  };

  const inverter1Data = generateHeatmapData(1);
  const inverter2Data = generateHeatmapData(2);
  const rows = 3;
  const cols = 14;

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 md:p-6">
      {/* Component Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {Object.entries(componentStatus).map(([key, value]) => (
          <Card key={key} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 capitalize mb-1">
                    {key === 'statcom' ? 'STATCOM' : key === 'pqm' ? 'PQM' : key} Status
                  </p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                    {value.active} / {value.total}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">Active Count</p>
                </div>
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                  <Activity className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Heatmap Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg sm:text-xl font-semibold">
            String Combiner Box Availability (%) Heatmap
          </CardTitle>
          <div className="flex items-center space-x-2">
            <span className="text-xs sm:text-sm bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full font-medium">
              LIVE
            </span>
            <Button onClick={fetchData} disabled={isLoading} size="sm" variant="outline">
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Block Selection */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Block
            </label>
            <select
              value={selectedBlock}
              onChange={(e) => setSelectedBlock(e.target.value)}
              className="w-full sm:w-auto min-w-[200px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              {blocks.map(block => (
                <option key={block} value={block}>Block - {block}</option>
              ))}
            </select>
          </div>

          {/* Inverter Heatmaps */}
          <div className="space-y-6">
            {/* Inverter 1 */}
            <div>
              <h3 className="text-sm sm:text-base font-semibold mb-3 text-gray-700 dark:text-gray-300">
                Inverter - 1
              </h3>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full">
                  <div className="grid gap-1 sm:gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                    {inverter1Data.map((value, index) => (
                      <div
                        key={index}
                        className={`${getColorClass(value)} p-2 sm:p-3 text-center text-xs sm:text-sm font-medium rounded transition-all hover:scale-105`}
                        style={{ minWidth: '50px' }}
                      >
                        {value === 0 ? '0' : value.toFixed(2)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Inverter 2 */}
            <div>
              <h3 className="text-sm sm:text-base font-semibold mb-3 text-gray-700 dark:text-gray-300">
                Inverter - 2
              </h3>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full">
                  <div className="grid gap-1 sm:gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                    {inverter2Data.map((value, index) => (
                      <div
                        key={index}
                        className={`${getColorClass(value)} p-2 sm:p-3 text-center text-xs sm:text-sm font-medium rounded transition-all hover:scale-105`}
                        style={{ minWidth: '50px' }}
                      >
                        {value === 0 ? '0' : value.toFixed(2)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Color Legend */}
          <div className="mt-4 sm:mt-6 flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-xs sm:text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Excellent (≥88%)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span>Good (86-88%)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-orange-300 rounded-full"></div>
              <span>Low (84-86%)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span>Critical (&lt;84%)</span>
            </div>
          </div>

          {/* Summary Statistics */}
          <div className="mt-4 sm:mt-6 grid grid-cols-3 gap-3 sm:gap-4">
            <div className="text-center p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">Average</p>
              <p className="text-lg sm:text-xl font-bold text-blue-600 dark:text-blue-400">
                {summary.average.toFixed(2)}
              </p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">Best Day</p>
              <p className="text-lg sm:text-xl font-bold text-green-600 dark:text-green-400">
                {summary.best.toFixed(2)}
              </p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">Worst Day</p>
              <p className="text-lg sm:text-xl font-bold text-red-600 dark:text-red-400">
                {summary.worst.toFixed(2)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StringCombinerBoxAvailability;




